import { InputErrorMessage as Error, RatingInput, Section } from '@/components';
import APP_CONFIG from '@/config/app';
import { submitGeneralRating } from '@/services/RatingsService';
import { submitRating } from '@/services/YieldBearingAssets';
import { logError, logInfo } from '@/utils/logger';
import { ErrorMessage } from '@hookform/error-message';
import { AnimatePresence } from 'framer-motion';
import { FC } from 'react';
import {
  FormProvider as ReactHookFormProvider,
  useForm,
} from 'react-hook-form';
const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;
export enum RatingFormType {
  YIELD_BEARING_ASSET = 'YIELD_BEARING_ASSET',
  GENERAL = 'GENERAL',
}
export enum YieldBearingAssetRatingType {
  ASSET_QUALITY = 'assetQuality',
  INVESTMENT_EXPERIENCE = 'investmentExperience',
}
export enum GeneralRatingType {
  APP_EXPERIENCE = 'appExperience',
}
export interface RatingDefinition {
  name: YieldBearingAssetRatingType | GeneralRatingType;
  label: string;
}
export const YIELD_BEARING_ASSET_RATINGS: RatingDefinition[] = [
  {
    name: YieldBearingAssetRatingType.ASSET_QUALITY,
    label: 'Asset Quality Rating',
  },
  {
    name: YieldBearingAssetRatingType.INVESTMENT_EXPERIENCE,
    label: 'Investment Experience Rating',
  },
];
export const GENERAL_RATINGS: RatingDefinition[] = [
  {
    name: GeneralRatingType.APP_EXPERIENCE,
    label: 'Overall Experience Rating',
  },
];
interface RatingFormProps {
  type: RatingFormType;
  ticker?: string;
  ratingTypes: RatingDefinition[];
  onRatingPosted?: () => void;
}
interface RatingFormData {
  [key: string]: number;
}
const RatingForm: FC<RatingFormProps> = ({
  type,
  ticker,
  ratingTypes,
  onRatingPosted: ratingPostedHandler,
}) => {
  const methods = useForm<RatingFormData>({
    defaultValues: ratingTypes.reduce(
      (acc, { name }) => ({
        ...acc,
        [name]: 0,
      }),
      {},
    ),
  });
  const {
    setValue,
    formState: { errors },
    clearErrors,
  } = methods;
  const handleRatingChange = async (name: string, value: number) => {
    setValue(name, value);
    await handleSubmitRating(name, value);
  };
  const handleSubmitRating = async (ratingType: string, rating: number) => {
    logInfo('Submitting rating', 'RatingForm.handleSubmitRating', {
      ratingType,
      rating,
    });
    try {
      if (type === RatingFormType.YIELD_BEARING_ASSET && ticker) {
        await submitRating(
          ticker,
          ratingType as 'assetQuality' | 'investmentExperience',
          rating,
        );
      } else {
        await submitGeneralRating(ratingType, rating);
      }
      logInfo('Rating submitted successfully', 'RatingForm.handleSubmitRating');
      if (ratingPostedHandler) {
        ratingPostedHandler();
      }
    } catch (error) {
      logError(error, 'RatingForm.handleSubmitRating');
    }
  };
  return (
    <ReactHookFormProvider {...methods}>
      <form className="flex flex-col w-full gap-md">
        {ratingTypes.map(({ name, label }) => (
          <Section
            key={name}
            body={
              <RatingInput
                value={methods.watch(name)}
                onChange={(value) => handleRatingChange(name, value)}
                name={name}
                errors={errors}
                clearErrors={clearErrors}
                showError
                label={label}
              />
            }
          />
        ))}
        <ErrorMessage
          errors={errors}
          name={ratingTypes[0].name}
          render={({ message }) =>
            message ? (
              <AnimatePresence>
                <Error
                  message={message}
                  className={`relative bottom-0.5 left-0 right-0 backdrop-blur${MAIN_BACKDROP_BLUR}`}
                />
              </AnimatePresence>
            ) : null
          }
        />
      </form>
    </ReactHookFormProvider>
  );
};
export default RatingForm;
